import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import Button from '@material-ui/core/Button';
import { makeStyles, Theme } from '@material-ui/core/styles';
import MailOutline from '@material-ui/icons/MailOutline';
import { DialogBox, Loader, usePrint } from '@mummssoftware/common-ui';
import { Printer } from '@mummssoftware/icons';
import { Printing } from '@mummssoftware/utils';

import { FormsContext } from '../../context';
import { FajitaContext } from './Context';
import { SET_ELECTION_ADDENDUM_PRINT_FORM } from './Context/constants';

import defaults from '../../translations/en.json';

const useStyles = makeStyles((theme: Theme) => ({
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    minWidth: 280,
  },
  actionButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontSize: '1rem',
    padding: theme.spacing(1.5, 2),
    borderColor: 'currentColor',
  },
  icon: {
    marginRight: theme.spacing(1.5),
    fontSize: '1.25rem',
  },
}));

type PrintDialogContentProps = {
  form: mumms.FormDTO;
  loading: boolean;
  onClose: () => void;
  setLoading: (loading: boolean) => void;
};

/**
 * Step 4: Print / Fax the saved EA form via printManifest (same as Questionnaires).
 * Mounted only while the dialog is open so usePrint/FaxProvider are not required when closed.
 */
const ElectionAddendumPrintDialogContent = ({
  form,
  loading,
  onClose,
  setLoading,
}: PrintDialogContentProps) => {
  const classes = useStyles();
  const { keycloak, agency, currentUser, patient } = useContext(FormsContext);
  const formRef = useRef(form);
  formRef.current = form;

  const printingService = useMemo(
    () =>
      keycloak && currentUser
        ? new Printing({
            url: window.env.hosts.printingService as string,
            keycloak,
            agency: agency as string,
            currentUser,
          })
        : null,
    [keycloak, agency, currentUser],
  );

  const canPrint = Boolean(keycloak && patient && printingService);

  const getPrintPromise = useCallback(
    (faxInstructions: any) =>
      printingService!.printManifest(
        [formRef.current],
        patient!,
        undefined,
        undefined,
        faxInstructions,
      ),
    [printingService, patient],
  );

  const printOrFax = usePrint(
    getPrintPromise,
    () => {
      // Keep loading on the parent so the Loader survives DialogBox unmount.
      setLoading(true);
      onClose();
    },
    () => setLoading(false),
  );

  const onPrintOrFax = (wantFax: boolean) => {
    if (!canPrint) {
      return;
    }
    printOrFax(wantFax);
  };

  return (
    <DialogBox
      title={defaults.printElectionAddendum}
      open
      handleClick={onClose}
    >
      <div className={classes.actions}>
        <Button
          className={classes.actionButton}
          variant="outlined"
          color="inherit"
          fullWidth
          disabled={!canPrint || loading}
          onClick={() => onPrintOrFax(false)}
          startIcon={<Printer className={classes.icon} />}
        >
          {defaults.print}
        </Button>
        <Button
          className={classes.actionButton}
          variant="outlined"
          color="inherit"
          fullWidth
          disabled={!canPrint || loading}
          onClick={() => onPrintOrFax(true)}
          startIcon={<MailOutline className={classes.icon} />}
        >
          {defaults.fax}
        </Button>
      </div>
    </DialogBox>
  );
};

/**
 * Print election addendum dialog after Left Hard Copy submit.
 * Loading lives here so the spinner stays until print/fax settles after the choice dialog closes.
 */
export const ElectionAddendumPrintDialog = () => {
  const { electionAddendumPrintForm, dispatch } = useContext(FajitaContext);
  const [loading, setLoading] = useState(false);

  const onClose = () => {
    dispatch({
      type: SET_ELECTION_ADDENDUM_PRINT_FORM,
      payload: null,
    });
  };

  if (!electionAddendumPrintForm && !loading) {
    return null;
  }

  return (
    <>
      {electionAddendumPrintForm && (
        <ElectionAddendumPrintDialogContent
          form={electionAddendumPrintForm}
          loading={loading}
          onClose={onClose}
          setLoading={setLoading}
        />
      )}
      {loading &&
        createPortal(
          <Loader />,
          document.getElementById('portal-container') || document.body,
        )}
    </>
  );
};
